'use client';

import type {
  ScreenHolderView,
  TerminalServerMessage
} from '@shared/all/domains/electron';
import { makeChunkReassembler } from '@shared/all/helpers/wireChunk';
import {
  base64ToBytes,
  type BoardAgentAPI,
  buildRemoteBoardAgent,
  type DataChannelInterface,
  type OpenTerminal,
  textToBase64
} from '@shared/utils/helpers/remoteBoardAgent';
import storage from '@shared/utils/helpers/storage';
import {
  type CardAIConfigAction,
  cardAIConfigInitialState,
  cardAIConfigReducer,
  type CardAIConfigState,
  type SetupPhase
} from '@shared/utils/reducers/cardAIConfigReducer';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useReducer,
  useState
} from 'react';

import {
  type RunnerConnectionState,
  type RunnerLinkDiagnostics
} from './useRunnerConnection';
import { type OnlineRunner, useRunners } from './useRunners';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

// Keyed on runner name - socketId changes on reconnect.
export const preferredRunnerKey = (spaceSlug: string) =>
  `preferredRunnerName:${spaceSlug}`;
// Persisted marker for an explicit "Local (this machine)" pick, so it isn't
// overridden by remote auto-select on the next load.
export const LOCAL_RUNNER = '__local__';

// Desktop uses Electron safeStorage, unlocked with the OS session: no vault to
// lock, so these fail loudly rather than silently no-op.
const DESKTOP_NO_VAULT =
  'This app stores credentials in your device keychain, which unlocks with your login. It has no connection password.';

// Single Electron-side terminal router. Frames arrive on the 'terminal' IPC
// channel; we reassemble, parse, and route by sessionId to the right handle.
let electronTerminalOpen: OpenTerminal | null = null;
function getElectronTerminalOpen(): OpenTerminal {
  if (electronTerminalOpen) return electronTerminalOpen;

  type Listeners = {
    data: Set<(chunk: Uint8Array) => void>;
    exit: Set<(info: { code: number | null; reason?: string }) => void>;
  };
  const terminals = new Map<string, Listeners>();
  const reassembler = makeChunkReassembler();

  window.electronAPI!.terminal.onFrame((frame) => {
    const wire = reassembler.feed(frame);
    if (wire === null) return;
    try {
      const msg = JSON.parse(wire) as TerminalServerMessage;
      if (msg.type !== 'terminal') return;
      const handle = terminals.get(msg.sessionId);
      if (!handle) return;
      if (msg.op === 'stdout') {
        const bytes = base64ToBytes(msg.data);
        for (const cb of handle.data) cb(bytes);
      } else if (msg.op === 'exit') {
        for (const cb of handle.exit)
          cb({ code: msg.code, reason: msg.reason });
        terminals.delete(msg.sessionId);
      } else if (msg.op === 'error') {
        for (const cb of handle.exit) cb({ code: null, reason: msg.message });
        terminals.delete(msg.sessionId);
      }
    } catch {
      // malformed frame
    }
  });

  let counter = 0;
  electronTerminalOpen = ({ postId, cols, rows, terminalKey }) => {
    const sessionId = `terminal-electron-${++counter}-${Date.now()}`;
    const listeners: Listeners = { data: new Set(), exit: new Set() };
    terminals.set(sessionId, listeners);

    window.electronAPI!.terminal.send({
      type: 'terminal',
      op: 'open',
      sessionId,
      postId,
      cols,
      rows,
      terminalKey
    });

    let closed = false;
    return {
      write: (data) => {
        if (closed) return;
        window.electronAPI!.terminal.send({
          type: 'terminal',
          op: 'stdin',
          sessionId,
          data: textToBase64(data)
        });
      },
      onData: (cb) => {
        listeners.data.add(cb);
        return () => listeners.data.delete(cb);
      },
      onExit: (cb) => {
        listeners.exit.add(cb);
        return () => listeners.exit.delete(cb);
      },
      resize: (cols, rows) => {
        if (closed) return;
        window.electronAPI!.terminal.send({
          type: 'terminal',
          op: 'resize',
          sessionId,
          cols,
          rows
        });
      },
      close: () => {
        if (closed) return;
        closed = true;
        window.electronAPI!.terminal.send({
          type: 'terminal',
          op: 'close',
          sessionId
        });
        terminals.delete(sessionId);
      }
    };
  };
  return electronTerminalOpen;
}

// Electron IPC transport - used when there's no remote runner.
function buildElectronAgent(spaceSlug: string): BoardAgentAPI {
  const { dispatch, on } = window.electronAPI!.boardAgent;
  return {
    getConfig: () => dispatch(spaceSlug, 'getConfig'),
    setConfig: (config) => dispatch(spaceSlug, 'setConfig', config),
    clearToken: (key) => dispatch(spaceSlug, 'clearToken', { key }),

    askBoard: (params) => dispatch(spaceSlug, 'ask', params),
    resetBoardSession: () => dispatch(spaceSlug, 'resetBoardSession'),
    boardSession: (conversationId) =>
      dispatch(spaceSlug, 'boardSession', { conversationId }),
    boardSessions: () => dispatch(spaceSlug, 'boardSessions'),
    boardSessionUpdate: (params) =>
      dispatch(spaceSlug, 'boardSessionUpdate', params),
    boardSessionDelete: (conversationId) =>
      dispatch(spaceSlug, 'boardSessionDelete', { conversationId }),
    boardHandoff: (conversationId) =>
      dispatch(spaceSlug, 'boardHandoff', { conversationId }),
    skillsList: () => dispatch(spaceSlug, 'skillsList'),
    skillRead: (params) => dispatch(spaceSlug, 'skillRead', params),
    skillWriteGlobal: (params) =>
      dispatch(spaceSlug, 'skillWriteGlobal', params),
    skillDeleteGlobal: (params) =>
      dispatch(spaceSlug, 'skillDeleteGlobal', params),
    skillProposeRepo: (params) =>
      dispatch(spaceSlug, 'skillProposeRepo', params),
    skillMigrate: (params) => dispatch(spaceSlug, 'skillMigrate', params),
    deployStatus: () => dispatch(spaceSlug, 'deployStatus'),
    deployBranches: () => dispatch(spaceSlug, 'deployBranches'),
    deployCreateSkill: (params) =>
      dispatch(spaceSlug, 'deployCreateSkill', params),
    deployReviseSkill: (params) =>
      dispatch(spaceSlug, 'deployReviseSkill', params),
    deployCreatorState: () => dispatch(spaceSlug, 'deployCreatorState'),
    deployLoadSkillBranch: (params) =>
      dispatch(spaceSlug, 'deployLoadSkillBranch', params),
    deployCheckEnvironment: () => dispatch(spaceSlug, 'deployCheckEnvironment'),
    deployFixEnvironment: (params) =>
      dispatch(spaceSlug, 'deployFixEnvironment', params),
    deployEnvState: () => dispatch(spaceSlug, 'deployEnvState'),
    onDeployEnv: (callback) => on(spaceSlug, 'deploy-env', callback),
    deployRunStart: (params) => dispatch(spaceSlug, 'deployRunStart', params),
    deployRunState: () => dispatch(spaceSlug, 'deployRunState'),
    deployRunAnswer: (params) => dispatch(spaceSlug, 'deployRunAnswer', params),
    setupAuthorize: (params) => dispatch(spaceSlug, 'setupAuthorize', params),
    deployRunAuthorize: (params) =>
      dispatch(spaceSlug, 'deployRunAuthorize', params),
    secretsAuthState: () => dispatch(spaceSlug, 'secretsAuthState'),
    secretsAuthAnswer: (params) =>
      dispatch(spaceSlug, 'secretsAuthAnswer', params),
    onSecretsAuth: (callback) => on(spaceSlug, 'secrets-auth', callback),
    dangerousApprovalState: () => dispatch(spaceSlug, 'dangerousApprovalState'),
    dangerousApprovalAnswer: (params) =>
      dispatch(spaceSlug, 'dangerousApprovalAnswer', params),
    secretsLock: () => dispatch(spaceSlug, 'secretsLock', {}),
    secretsExportBackup: (params) =>
      dispatch(spaceSlug, 'secretsExportBackup', params),
    secretsOpenBackup: (params) =>
      dispatch(spaceSlug, 'secretsOpenBackup', params),
    secretsImportBackupPlan: (params) =>
      dispatch(spaceSlug, 'secretsImportBackupPlan', params),
    secretsImportBackupApply: (params) =>
      dispatch(spaceSlug, 'secretsImportBackupApply', params),
    secretsChangePassword: (params) =>
      dispatch(spaceSlug, 'secretsChangePassword', params),
    runnerAccessGet: () => dispatch(spaceSlug, 'runnerAccessGet'),
    runnerAccessSet: (params) => dispatch(spaceSlug, 'runnerAccessSet', params),
    onRunnerAccess: (callback) => on(spaceSlug, 'runner-access', callback),
    jobsList: () => dispatch(spaceSlug, 'jobsList'),
    attention: () => dispatch(spaceSlug, 'attention'),
    serviceStatus: () => dispatch(spaceSlug, 'serviceStatus'),
    serviceInstall: () => dispatch(spaceSlug, 'serviceInstall'),
    serviceRemove: () => dispatch(spaceSlug, 'serviceRemove'),
    partsList: () => dispatch(spaceSlug, 'partsList'),
    partsAct: (params) => dispatch(spaceSlug, 'partsAct', params),
    machineCleanup: (params) => dispatch(spaceSlug, 'machineCleanup', params),
    vaultStatus: () => dispatch(spaceSlug, 'vaultStatus', {}),
    vaultShareSet: (params) => dispatch(spaceSlug, 'vaultShareSet', params),
    jobsCall: (params) => dispatch(spaceSlug, 'jobsCall', params),
    jobStatus: (params) => dispatch(spaceSlug, 'jobStatus', params),
    jobCancel: (params) => dispatch(spaceSlug, 'jobCancel', params),
    jobsActive: () => dispatch(spaceSlug, 'jobsActive'),
    auditRead: (params) => dispatch(spaceSlug, 'auditRead', params),
    auditClear: () => dispatch(spaceSlug, 'auditClear', {}),
    // Keep-awake belongs to a runner; the desktop's own agent has none.
    keepAwakeSet: (payload) => dispatch(spaceSlug, 'keepAwakeSet', payload),
    secretsInboxList: () => dispatch(spaceSlug, 'secretsInboxList'),
    secretsInboxAnswer: (params) =>
      dispatch(spaceSlug, 'secretsInboxAnswer', params),
    deployRunDeny: () => dispatch(spaceSlug, 'deployRunDeny'),
    deployRunCancel: () => dispatch(spaceSlug, 'deployRunCancel'),
    deployRunDismiss: () => dispatch(spaceSlug, 'deployRunDismiss'),
    onDeployRun: (callback) => on(spaceSlug, 'deploy-run', callback),
    onDeployCreator: (callback) => on(spaceSlug, 'deploy-creator', callback),
    deployApproveSkill: () => dispatch(spaceSlug, 'deployApproveSkill'),
    deployDiscardSkill: () => dispatch(spaceSlug, 'deployDiscardSkill'),
    deploySecretsStatus: () => dispatch(spaceSlug, 'deploySecretsStatus'),
    deploySecretsInit: (params) =>
      dispatch(spaceSlug, 'deploySecretsInit', params),
    deploySecretsList: (params) =>
      dispatch(spaceSlug, 'deploySecretsList', params),
    deploySecretsSet: (params) =>
      dispatch(spaceSlug, 'deploySecretsSet', params),
    deploySecretsSetMeta: (params) =>
      dispatch(spaceSlug, 'deploySecretsSetMeta', params),
    deploySecretsUnset: (params) =>
      dispatch(spaceSlug, 'deploySecretsUnset', params),
    deploySecretsSetFile: (params) =>
      dispatch(spaceSlug, 'deploySecretsSetFile', params),
    deploySecretsUnsetFile: (params) =>
      dispatch(spaceSlug, 'deploySecretsUnsetFile', params),
    secretFileEnvKeys: (params) =>
      dispatch(spaceSlug, 'secretFileEnvKeys', params),
    secretFileEnvSet: (params) =>
      dispatch(spaceSlug, 'secretFileEnvSet', params),
    secretFileEnvUnset: (params) =>
      dispatch(spaceSlug, 'secretFileEnvUnset', params),
    setupStatus: () => dispatch(spaceSlug, 'setupStatus'),
    environmentStatus: () => dispatch(spaceSlug, 'environmentStatus'),
    ensureSetup: () => dispatch(spaceSlug, 'ensureSetup'),
    cardExecute: (params) => dispatch(spaceSlug, 'cardExecute', params),
    cardMergeContent: (params) =>
      dispatch(spaceSlug, 'cardMergeContent', params),
    cardMergeSessions: (params) =>
      dispatch(spaceSlug, 'cardMergeSessions', params),
    cardStartPreview: (postId) =>
      dispatch(spaceSlug, 'cardStartPreview', { postId }),
    cardSessions: () => dispatch(spaceSlug, 'cardSessions'),
    cardSession: (postId) => dispatch(spaceSlug, 'cardSession', { postId }),
    cardDismiss: (postId) => dispatch(spaceSlug, 'cardDismiss', { postId }),
    cardRemoteBranches: () => dispatch(spaceSlug, 'cardRemoteBranches'),
    bareWorkNames: () => dispatch(spaceSlug, 'bareWorkNames'),
    onedriveStartDeviceCode: (params) =>
      dispatch(spaceSlug, 'onedriveStartDeviceCode', params),
    onedriveDisconnect: () => dispatch(spaceSlug, 'onedriveDisconnect'),
    githubStartDeviceCode: (params) =>
      dispatch(spaceSlug, 'githubStartDeviceCode', params),
    githubDisconnect: () => dispatch(spaceSlug, 'githubDisconnect'),
    githubIdentity: () => dispatch(spaceSlug, 'githubIdentity'),
    githubListRepos: () => dispatch(spaceSlug, 'githubListRepos'),
    githubListBranches: (params) =>
      dispatch(spaceSlug, 'githubListBranches', params),
    onGithubAuth: (callback) => on(spaceSlug, 'github-auth', callback),
    onOneDriveAuth: (callback) => on(spaceSlug, 'onedrive-auth', callback),
    claudeLoginStart: () => dispatch(spaceSlug, 'claudeLoginStart'),
    claudeLoginCode: (code) => dispatch(spaceSlug, 'claudeLoginCode', { code }),
    claudeLoginCancel: () => dispatch(spaceSlug, 'claudeLoginCancel'),
    claudeLogout: () => dispatch(spaceSlug, 'claudeLogout'),
    onClaudeAuth: (callback) => on(spaceSlug, 'claude-auth', callback),
    codexLoginStart: () => dispatch(spaceSlug, 'codexLoginStart'),
    codexLoginCancel: () => dispatch(spaceSlug, 'codexLoginCancel'),
    codexLogout: () => dispatch(spaceSlug, 'codexLogout'),
    activityDismiss: (id) => dispatch(spaceSlug, 'activityDismiss', { id }),
    onCodexAuth: (callback) => on(spaceSlug, 'codex-auth', callback),
    onOneDriveIndex: (callback) => on(spaceSlug, 'onedrive-index', callback),
    onedriveIndexStatus: () => dispatch(spaceSlug, 'onedriveIndexStatus'),
    onedriveReindex: () => dispatch(spaceSlug, 'onedriveReindex'),
    onedriveBrowseFolders: (params) =>
      dispatch(spaceSlug, 'onedriveBrowseFolders', params),
    onedriveSelectFolder: (params) =>
      dispatch(spaceSlug, 'onedriveSelectFolder', params),
    onedriveUnselectFolder: (params) =>
      dispatch(spaceSlug, 'onedriveUnselectFolder', params),
    cardFinalize: (params) => dispatch(spaceSlug, 'cardFinalize', params),
    cardAnswerQuestion: (postId, answer) =>
      dispatch(spaceSlug, 'cardAnswerQuestion', { postId, answer }),
    cardFeedback: (postId, feedback) =>
      dispatch(spaceSlug, 'cardFeedback', { postId, feedback }),
    cardKill: (postId) => dispatch(spaceSlug, 'cardKill', { postId }),
    cardHandoff: (postId) => dispatch(spaceSlug, 'cardHandoff', { postId }),

    bareFsList: (params) => dispatch(spaceSlug, 'bareFsList', params),
    bareFsSearch: (params) => dispatch(spaceSlug, 'bareFsSearch', params),
    bareFsRead: (params) => dispatch(spaceSlug, 'bareFsRead', params),
    bareFsWrite: (params) => dispatch(spaceSlug, 'bareFsWrite', params),
    bareFsDelete: (params) => dispatch(spaceSlug, 'bareFsDelete', params),
    bareFsMkdir: (params) => dispatch(spaceSlug, 'bareFsMkdir', params),
    terminalDropImage: (params) =>
      dispatch(spaceSlug, 'terminalDropImage', params),
    terminalDropFile: (params) =>
      dispatch(spaceSlug, 'terminalDropFile', params),
    bareReview: (params) => dispatch(spaceSlug, 'bareReview', params),
    bareGitStatus: (params) => dispatch(spaceSlug, 'bareGitStatus', params),
    bareGitCommitDiff: (params) =>
      dispatch(spaceSlug, 'bareGitCommitDiff', params),
    bareGitCompare: (params) => dispatch(spaceSlug, 'bareGitCompare', params),
    bareGitStage: (params) => dispatch(spaceSlug, 'bareGitStage', params),
    bareGitUnstage: (params) => dispatch(spaceSlug, 'bareGitUnstage', params),
    bareListTerminals: (params) =>
      dispatch(spaceSlug, 'bareListTerminals', params),
    bareTerminalHealth: (params) =>
      dispatch(spaceSlug, 'bareTerminalHealth', params),
    bareAckTerminalDigest: () =>
      dispatch(spaceSlug, 'bareAckTerminalDigest', {}),
    bareRestartStaleTerminals: (params) =>
      dispatch(spaceSlug, 'bareRestartStaleTerminals', params),
    bareListProcesses: (params) =>
      dispatch(spaceSlug, 'bareListProcesses', params),
    bareKillProcess: (params) => dispatch(spaceSlug, 'bareKillProcess', params),
    bareEditorState: (params) => dispatch(spaceSlug, 'bareEditorState', params),
    bareOpenFile: (params) => dispatch(spaceSlug, 'bareOpenFile', params),
    bareCloseFile: (params) => dispatch(spaceSlug, 'bareCloseFile', params),
    bareBufferSet: (params) => dispatch(spaceSlug, 'bareBufferSet', params),
    bareExpose: (params) => dispatch(spaceSlug, 'bareExpose', params),
    bareUnexpose: (params) => dispatch(spaceSlug, 'bareUnexpose', params),
    bareListExposed: (params) => dispatch(spaceSlug, 'bareListExposed', params),

    simulatorList: () => dispatch(spaceSlug, 'simulatorList', {}),
    simulatorStatus: () => dispatch(spaceSlug, 'simulatorStatus', {}),
    simulatorAttach: (payload) =>
      dispatch(spaceSlug, 'simulatorAttach', payload),
    simulatorDetach: () => dispatch(spaceSlug, 'simulatorDetach', {}),
    simulatorInput: (payload) => dispatch(spaceSlug, 'simulatorInput', payload),
    screenStatus: (params) => dispatch(spaceSlug, 'screenStatus', params),
    screenAttach: (params) => dispatch(spaceSlug, 'screenAttach', params),
    screenDetach: () => dispatch(spaceSlug, 'screenDetach', {}),
    screenInput: (payload) => dispatch(spaceSlug, 'screenInput', payload),

    scratchpadList: () => dispatch(spaceSlug, 'scratchpadList', {}),
    scratchpadRead: (params) => dispatch(spaceSlug, 'scratchpadRead', params),
    scratchpadReadChunk: (params) =>
      dispatch(spaceSlug, 'scratchpadReadChunk', params),
    scratchpadDelete: (params) =>
      dispatch(spaceSlug, 'scratchpadDelete', params),
    scratchpadClear: () => dispatch(spaceSlug, 'scratchpadClear', {}),

    // Nothing to unlock on desktop, so report the state and refuse the
    // unlock rather than pretend it worked.
    secureStatus: () => dispatch(spaceSlug, 'secureStatus'),
    secureUnlock: () => Promise.reject(new Error(DESKTOP_NO_VAULT)),
    onSecureState: () => () => {},

    terminal: getElectronTerminalOpen(),
    killTerminal: ({ postId, terminalKey }) =>
      window.electronAPI!.terminal.send({
        type: 'terminal',
        op: 'kill',
        postId,
        terminalKey
      }),
    restartTerminal: ({ postId, terminalKey }) =>
      window.electronAPI!.terminal.send({
        type: 'terminal',
        op: 'restart',
        postId,
        terminalKey
      }),

    onBoardLog: (callback) => on(spaceSlug, 'log', callback),
    onStatus: (callback) => on(spaceSlug, 'status', callback),
    onSetupStatus: (callback) => on(spaceSlug, 'setup-status', callback),
    onSessionUpdate: (callback) => on(spaceSlug, 'session-update', callback),
    onActivityUpdate: (callback) => on(spaceSlug, 'activity-update', callback),
    onLog: (postId, callback) => on(spaceSlug, `log:${postId}`, callback),
    onBareFsChange: (postId, callback) =>
      on(spaceSlug, `bare-fs:${postId}`, callback),
    onScratchpad: (callback) => on(spaceSlug, 'scratchpad', callback),

    // Electron-local agent has no peer devices - broadcasts are no-ops.
    broadcast: () => {},
    onBoardUpdate: () => () => {}
  };
}

type RunnerContextValue = {
  runners: OnlineRunner[];
  selectedRunner: string | null;
  selectRunner: (socketId: string | null) => void;
  connectionState: RunnerConnectionState;
  /** Why a failed link failed. Null when no runner is selected. */
  connectionDiagnostics: RunnerLinkDiagnostics | null;
  /** Dial the selected runner again after a failure. No-op while none is
   *  selected, or for the local Electron agent, which has no handshake. */
  retryConnection: () => void;
  remoteChannel: DataChannelInterface | null;
  /** The simulator's video, when the selected runner is sending one. */
  simulatorStream: MediaStream | null;
  /** Who has the selected runner's video stream. Null when nobody does. */
  screenHolder: ScreenHolderView | null;
  agent: BoardAgentAPI | null;
  /** Agent for any runner (socketId, or null = local Electron), so a feature
   *  can target one other than the space's pick. Null if it isn't connected. */
  agentFor: (socketId: string | null) => BoardAgentAPI | null;
  config: CardAIConfigState;
  configDispatch: React.Dispatch<CardAIConfigAction>;
  triggerSetup: () => Promise<boolean>;
  spaceSlug: string;
  spaceId?: number;
};

// Exported so the onboarding demo can provide a fixture runner/agent
export const RunnerContext = createContext<RunnerContextValue>({
  runners: [],
  selectedRunner: null,
  selectRunner: () => {},
  connectionState: 'idle',
  connectionDiagnostics: null,
  retryConnection: () => {},
  remoteChannel: null,
  simulatorStream: null,
  screenHolder: null,
  agent: null,
  agentFor: () => null,
  config: cardAIConfigInitialState,
  configDispatch: () => {},
  triggerSetup: async () => false,
  spaceSlug: ''
});

// One agent per (socketId, spaceSlug, spaceId), shared across providers.
// channel.onMessage is additive, so a second agent on one channel would
// duplicate the handler — and the Running jobs panel can mount a second
// RunnerProvider for a space the board already has open, so this cache has to
// outlive any single provider instance.
const remoteAgents = new Map<
  string,
  {
    channel: DataChannelInterface;
    spaceId?: number;
    agent: BoardAgentAPI;
  }
>();
const electronAgents = new Map<string, BoardAgentAPI>();

function getElectronAgent(spaceSlug: string): BoardAgentAPI | null {
  if (typeof window === 'undefined' || !window.electronAPI?.boardAgent) {
    return null;
  }
  let agent = electronAgents.get(spaceSlug);
  if (!agent) {
    agent = buildElectronAgent(spaceSlug);
    electronAgents.set(spaceSlug, agent);
  }
  return agent;
}

function getRemoteAgent(
  socketId: string,
  spaceSlug: string,
  spaceId: number | undefined,
  channel: DataChannelInterface
): BoardAgentAPI {
  const key = `${socketId}::${spaceSlug}`;
  const cached = remoteAgents.get(key);
  // A caller without a spaceId (the global secrets and approval gates) takes
  // the agent the board built with one. Rebuilding on the mismatch made the
  // gate and the open board evict each other on every render: while terminals
  // worked in the background each update built two more agents, each adding a
  // handler to the channel, and the password prompt flickered.
  if (
    cached &&
    cached.channel === channel &&
    (spaceId === undefined || cached.spaceId === spaceId)
  ) {
    return cached.agent;
  }
  const built = buildRemoteBoardAgent(spaceSlug, channel, spaceId);
  remoteAgents.set(key, { channel, spaceId, agent: built });
  return built;
}

/** A board's agent on a runner, for a caller outside any RunnerProvider (the
 *  X on a Running jobs row). It takes whatever agent the board already has on
 *  that channel, whichever spaceId built it, so a click never builds a second
 *  agent on the channel and evicts the board's own. */
export function remoteAgentOn(
  socketId: string,
  spaceSlug: string,
  channel: DataChannelInterface
): BoardAgentAPI {
  const cached = remoteAgents.get(`${socketId}::${spaceSlug}`);
  if (cached && cached.channel === channel) return cached.agent;
  return getRemoteAgent(socketId, spaceSlug, undefined, channel);
}

export function RunnerProvider({
  children,
  spaceSlug,
  spaceId,
  pinnedRunnerId
}: {
  children: ReactNode;
  spaceSlug: string;
  spaceId?: number;
  /** Pin this provider to one runner instead of the space's stored pick — the
   *  Running jobs overlay must talk to the machine the job is actually on.
   *  Keyed on runnerId, not socketId, so a reconnect re-resolves it. */
  pinnedRunnerId?: number;
}) {
  const {
    runners,
    channels,
    streams,
    states,
    diagnostics,
    retries,
    screenHolders
  } = useRunners();
  // undefined = no pick this session; null = explicit "Local" pick.
  const [userSelection, setUserSelection] = useState<string | null | undefined>(
    undefined
  );
  const [preferredName, setPreferredName] = useState<string | null>(null);

  // Reload preference when spaceSlug changes (provider doesn't unmount).
  useEffect(() => {
    let ignore = false;
    setPreferredName(null);
    setUserSelection(undefined);
    void storage.getItem(preferredRunnerKey(spaceSlug)).then((v) => {
      if (!ignore && v) setPreferredName(v);
    });
    return () => {
      ignore = true;
    };
  }, [spaceSlug]);

  // The user's pick if online, else by preferred name (socketIds churn on
  // reconnect). Never a substitute: with no pick, or the picked runner offline,
  // nothing is selected. Work lives on a machine — files, ports, git state — so
  // moving it to another one is the user's call, not ours.
  const explicitLocal =
    pinnedRunnerId === undefined &&
    (userSelection === null ||
      (userSelection === undefined && preferredName === LOCAL_RUNNER));
  let selectedRunner: string | null = null;
  if (pinnedRunnerId !== undefined) {
    // A pinned provider ignores the stored pick entirely — the job runs where
    // it runs, and pointing at another machine would open an empty session.
    selectedRunner =
      runners.find((r) => r.runnerId === pinnedRunnerId)?.socketId ?? null;
  } else if (
    userSelection != null &&
    runners.some((r) => r.socketId === userSelection)
  ) {
    selectedRunner = userSelection;
  } else if (!explicitLocal && preferredName) {
    const match = runners.find((r) => r.name === preferredName);
    if (match) selectedRunner = match.socketId;
  }

  // Channel for the selected runner is owned at app root and lives across
  // space navigations. We just look it up here.
  const remoteChannel = selectedRunner
    ? (channels[selectedRunner] ?? null)
    : null;
  const simulatorStream = selectedRunner
    ? (streams[selectedRunner] ?? null)
    : null;
  const selectedRunnerId = runners.find(
    (r) => r.socketId === selectedRunner
  )?.runnerId;
  const screenHolder =
    selectedRunnerId === undefined
      ? null
      : (screenHolders[selectedRunnerId] ?? null);
  // Read the link's own state rather than inferring one from channel presence:
  // inferring cannot express `failed`, so a dead handshake looked identical to
  // one still in progress and the UI spun forever with no error and no retry.
  // The link is absent for a render between selecting a runner and RunnerLink
  // reporting, which reads as 'connecting'.
  const connectionState: RunnerConnectionState = !selectedRunner
    ? 'idle'
    : (states[selectedRunner] ?? 'connecting');
  const connectionDiagnostics = selectedRunner
    ? (diagnostics[selectedRunner] ?? null)
    : null;
  const retryConnection = () => {
    if (selectedRunner) retries[selectedRunner]?.();
  };

  const selectRunner = (socketId: string | null) => {
    setUserSelection(socketId);
    if (socketId === null) {
      setPreferredName(LOCAL_RUNNER);
      void storage.setItem(preferredRunnerKey(spaceSlug), LOCAL_RUNNER);
      return;
    }
    const match = runners.find((r) => r.socketId === socketId);
    if (match) {
      setPreferredName(match.name);
      void storage.setItem(preferredRunnerKey(spaceSlug), match.name);
    }
  };

  // The only place a runner becomes an agent. Local counts as a pick, not a
  // fallback, so an offline runner leaves this null rather than reaching for
  // whatever machine is nearest.
  let agent: BoardAgentAPI | null = null;
  if (typeof window !== 'undefined') {
    if (remoteChannel && selectedRunner) {
      agent = getRemoteAgent(selectedRunner, spaceSlug, spaceId, remoteChannel);
    } else if (explicitLocal) {
      agent = getElectronAgent(spaceSlug);
    }
  }

  // Null means the selected runner; a socketId targets another machine, so
  // different boards can run on different runners at once.
  const agentFor = (socketId: string | null): BoardAgentAPI | null => {
    if (socketId === null) return agent;
    const channel = channels[socketId];
    return channel
      ? getRemoteAgent(socketId, spaceSlug, spaceId, channel)
      : null;
  };

  // Dev-only console handle for actions with no UI, keyed by board:
  //   await factiiiAgent['my-board'].setConfig({ githubToken: 'github_pat_…' })
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || !agent) return;
    const w = window as unknown as {
      factiiiAgent?: Record<string, BoardAgentAPI>;
    };
    w.factiiiAgent = { ...w.factiiiAgent, [spaceSlug]: agent };
    return () => {
      const map = w.factiiiAgent;
      if (map) delete map[spaceSlug];
    };
  }, [agent, spaceSlug]);

  // Provider owns config + setup state so gate and modal don't each run their
  // own setupStatus subscription.
  const [config, configDispatch] = useReducer(
    cardAIConfigReducer,
    cardAIConfigInitialState
  );
  const setSetup = (setup: SetupPhase) =>
    configDispatch({ type: 'SET_SETUP', setup });

  useEffect(() => {
    if (!agent) {
      setSetup({ phase: 'checking' });
      return;
    }
    let cancelled = false;

    const refresh = async () => {
      try {
        // Load config first so the connection state is populated even when already ready.
        const cfg = await agent.getConfig();
        if (cancelled) return;
        configDispatch({ type: 'LOADED_CONFIG', config: cfg });

        const { phase } = await agent.setupStatus();
        if (cancelled) return;
        if (phase === 'ready') return setSetup({ phase: 'ready' });
        if (phase === 'running')
          return setSetup({ phase: 'running', status: 'Setting up...' });

        // A space is set up once either source (git repo or OneDrive) is connected.
        const configured =
          Boolean(cfg.hasGithubToken && cfg.repoUrl) ||
          (cfg.onedrive?.connected ?? false);
        if (!configured) {
          return setSetup({ phase: 'idle' });
        }
        setSetup({ phase: 'running', status: 'Starting...' });
        try {
          await agent.ensureSetup();
          if (!cancelled) setSetup({ phase: 'ready' });
        } catch (err) {
          if (!cancelled) setSetup({ phase: 'error', error: errMsg(err) });
        }
      } catch {
        if (!cancelled) setSetup({ phase: 'idle' });
      }
    };

    void refresh();

    // Empty msg = engine signals completion; re-poll for the final phase.
    const unsub = agent.onSetupStatus((msg) => {
      if (cancelled) return;
      if (msg) configDispatch({ type: 'SETUP_PROGRESS', status: msg });
      else void refresh();
    });

    // An unlock - here or on another device - changes what the runner can
    // read, so the settings it reported as empty have to be asked for again.
    const unsubSecure = agent.onSecureState(() => {
      if (!cancelled) void refresh();
    });

    return () => {
      cancelled = true;
      unsub();
      unsubSecure();
    };
  }, [agent]);

  const triggerSetup = async (): Promise<boolean> => {
    if (!agent) return false;
    setSetup({ phase: 'running', status: 'Starting...' });
    try {
      await agent.ensureSetup();
      setSetup({ phase: 'ready' });
      return true;
    } catch (err) {
      setSetup({ phase: 'error', error: errMsg(err) });
      return false;
    }
  };

  return (
    <RunnerContext.Provider
      value={{
        runners,
        selectedRunner,
        selectRunner,
        connectionState,
        connectionDiagnostics,
        retryConnection,
        remoteChannel,
        simulatorStream,
        screenHolder,
        agent,
        agentFor,
        config,
        configDispatch,
        triggerSetup,
        spaceSlug,
        spaceId
      }}
    >
      {children}
    </RunnerContext.Provider>
  );
}

export function useRunner() {
  return useContext(RunnerContext);
}
